import {
    ChartAxisDirection,
    Debug,
    type Logger,
    ScaleAlignment,
    attachDescription,
    clamp,
    deepClone,
    defined,
    definedZoomState,
    isDate,
    isFiniteNumber,
    isNumericValue,
    isObject,
    isString,
    isValidDate,
    objectsEqual,
    pickDirectionZoom,
    strictObjectKeys,
    toZoomState,
    validate,
} from 'ag-charts-core';
import type {
    AxisID,
    BoxBounds,
    CartesianAxisDirection,
    DeepReadonly,
    DefinedZoomState,
    DynamicContext,
    MementoOriginator,
    OptionsDefs,
    Scale,
    ZoomMinMax,
    ZoomState,
} from 'ag-charts-core';
import type { AgZoomEvent, AgZoomEventSource } from 'ag-charts-types';

import type {
    ZoomChangeRequestEvent,
    ZoomChangeState,
    ZoomEventSourceDetail,
    ZoomMemento,
    ZoomMementoRange,
} from '../../core/eventsHub';
import type { ChartRegistry } from '../../module/moduleContext';
import { ContinuousScale } from '../../scale/continuousScale';
import { DiscreteTimeScale } from '../../scale/discreteTimeScale';
import type { BBox } from '../../scene/bbox';
import { BaseManager } from '../../util/baseManager';
import { PanToBBoxScalingModeEnum, calcPanToBBoxRatios } from '../../util/panToBBox';
import { rangeAlignment } from '../rangeAlignment';
import type { ISeries } from '../series/seriesTypes';

type CoreZoomEntry = ZoomMinMax & { direction: CartesianAxisDirection };
export type CoreZoomState = Record<AxisID, CoreZoomEntry>;
export type CoreZoomStateSafeRetrieval = { readonly [K in AxisID]: CoreZoomEntry | undefined };

export type SimpleAxis = {
    id: AxisID;
    direction: CartesianAxisDirection;
};

export type CartesianAxisLike = SimpleAxis & {
    type: string;
    visibleRange: [number, number];
    scale: Scale<any, any>;
    range: [number, number];
    boundSeries: ISeries<any, any, any>[];
    // User-supplied `min`/`max` bounds, read by zoom autoScaling to detect a fixed
    // domain. `NumberAxis`, `TimeAxis`, and `LogAxis` populate these via `axis.options.min/max`;
    // axis types without numeric bounds (e.g. category) leave `options` shaped without these keys.
    options?: { min?: number; max?: number };
    // Axis-local zoom — added during the zoom-state-migration work. `CartesianAxis` implements
    // these; `SimpleAxis` literals (e.g. topologyChart) do not, and ZoomManager must guard with
    // `'setZoom' in axis`.
    getZoom(): ZoomMinMax;
    setZoom(zoom: ZoomMinMax): void;
};

const rangeValidator = (axis?: CartesianAxisLike) =>
    attachDescription((value, { options }) => {
        if (!ContinuousScale.is(axis?.scale) && !DiscreteTimeScale.is(axis?.scale)) return true;
        if (value == null || options.end == null) return true;
        return value < options.end;
    }, `to be less than end`);

function validateChanges(changes: UpdateZoomChanges, logger: Logger): void {
    for (const axisId of strictObjectKeys(changes)) {
        const zoom = changes[axisId];
        if (!zoom) continue;

        const { min, max } = zoom;

        if (min < 0 || max > 1) {
            logger.warnOnce(
                `Attempted to update axis (${axisId}) zoom to an invalid ratio of [{ min: ${min}, max: ${max} }], expecting a ratio of 0 to 1. Ignoring.`
            );

            // Remove invalid zoom state for this axis
            delete changes[axisId];
        }
    }
}

export type UpdateZoomSourcing = {
    source: AgZoomEventSource;
    sourceDetail: ZoomEventSourceDetail;
};
export type UpdateZoomChanges = Record<AxisID, ZoomMinMax | undefined>;
export type UpdateZoomParams = UpdateZoomSourcing & {
    isReset: boolean;
    changes: UpdateZoomChanges;
};

export interface UpdateZoomWithParams {
    start: Date | number;
    end: Date | number;
    windowStart: Date | number;
    windowEnd: Date | number;
    source: AgZoomEventSource;
}

export type UpdateZoomWithFunction = (
    params: UpdateZoomWithParams
) => [Date | number | undefined, Date | number | undefined];

function refreshCoreState(nextAxes: Array<CartesianAxisLike> | Array<SimpleAxis>, state: CoreZoomStateSafeRetrieval) {
    const result: CoreZoomState = {};
    for (const { id, direction } of nextAxes) {
        const { min, max } = state[id] ?? { min: 0, max: 1 };
        result[id] = { min, max, direction };
    }
    return result;
}

function areEqualCoreZooms(p: CoreZoomStateSafeRetrieval, q: CoreZoomStateSafeRetrieval) {
    const pKeys = strictObjectKeys(p);
    const qKeys = strictObjectKeys(q);

    // Check that pKeys & qKeys are the same set of strings:
    if (pKeys.length !== qKeys.length) return false;
    for (const k of pKeys) if (!qKeys.includes(k)) return false;

    for (const k of pKeys) {
        const pVal = p[k];
        const qVal = q[k];
        if (pVal === qVal) {
            continue;
        } else if (
            pVal == undefined ||
            pVal.direction !== qVal?.direction ||
            pVal.min !== qVal.min ||
            pVal.max !== qVal.max
        ) {
            return false;
        }
    }
    return true;
}

export function userInteraction<D extends ZoomEventSourceDetail>(sourceDetail: D) {
    return { source: 'user-interaction' as const, sourceDetail };
}

function getGrouping(d: ZoomMementoRange['start' | 'end']) {
    if (isNumericValue(d) || isString(d) || isDate(d)) {
        return { value: d, groupPercentage: 0 };
    }
    return d ?? { value: undefined, groupPercentage: 0 };
}

function isGrouping(d: ZoomMementoRange['start' | 'end']) {
    return isObject(d) && 'groupPercentage' in d;
}

/**
 * Manages the current zoom state for a chart. Tracks the requested zoom from distinct dependents
 * and handles conflicting zoom requests.
 */
export class ZoomManager extends BaseManager implements MementoOriginator<ZoomMemento> {
    public mementoOriginatorKey = 'zoom' as const;

    private get state(): CoreZoomStateSafeRetrieval {
        // Rich axes own their zoom via getZoom(); simple axes (topologyChart) fall back to the
        // direction zoom in chartState since they cannot carry per-axis state.
        const directionZoom = this.ctx.chartState.getValue('zoom');
        const result: CoreZoomState = {};
        for (const axis of this.allAxes) {
            const rich = this.axes.find((a) => a.id === axis.id);
            let min: number;
            let max: number;
            if (rich) {
                ({ min, max } = rich.getZoom());
            } else {
                const fallback = pickDirectionZoom(directionZoom, axis.direction);
                min = fallback?.min ?? 0;
                max = fallback?.max ?? 1;
            }
            result[axis.id] = { min, max, direction: axis.direction };
        }
        return result;
    }

    private set state(value: CoreZoomStateSafeRetrieval) {
        const realAxisIds = new Set<string>();
        for (const axis of this.axes) {
            const entry = value[axis.id];
            realAxisIds.add(axis.id);
            if (!entry) continue;
            axis.setZoom({ min: entry.min, max: entry.max });
        }

        const syntheticIds = this.allAxes.filter((a) => !realAxisIds.has(a.id));
        if (syntheticIds.length === 0) return;

        const syntheticState: CoreZoomState = {};
        for (const axis of syntheticIds) {
            const entry = value[axis.id];
            if (entry) {
                syntheticState[axis.id] = entry;
            }
        }
        const nextZoom = toZoomState(syntheticState);
        if (nextZoom != null) {
            this.ctx.chartState.setValue('zoom', nextZoom);
        }
    }

    private readonly axes: CartesianAxisLike[] = [];
    private readonly allAxes: SimpleAxis[] = [];
    private didLayoutAxes = false;
    private pendingZoomEventSource?: AgZoomEventSource;

    private lastRestoredRequiredRange?: number; // The ratio (_requiredRange / dimension) last applied to zoom
    private lastRestoredRequiredRangeDirection?: CartesianAxisDirection;
    private lastRequiredRange?: number; // The raw pixel value from processRanges(), used to detect genuine option changes
    private restoreRequiredRangeIterations = 0;
    private independentAxes = false;
    private navigatorModule = false;
    private zoomModule = false;
    private readonly debug = Debug.create(true, 'zoom');

    public panToBBoxScalingMode = PanToBBoxScalingModeEnum.WhenViewportTooSmallScaleXYDisproportionally;

    // The initial state memento can not be restored until the chart has performed its first layout. Instead save it as
    // pending and restore then delete it on the first layout.
    private pendingMemento:
        | {
              version: string;
              mementoVersion: string;
              memento: ZoomMemento | undefined;
          }
        | undefined = undefined;

    constructor(private readonly ctx: DynamicContext<ChartRegistry>) {
        super();

        this.cleanup.register(
            ctx.eventsHub.on('zoom:change-request', (event) => {
                this.constrainZoomToRequiredWidth(event);
            }),
            ctx.eventsHub.on('update:pre-series', ({ requiredRangeRatio, requiredRangeDirection, requiredRange }) => {
                this.didLayoutAxes = true;

                const { pendingMemento } = this;
                if (pendingMemento) {
                    this.restoreMemento(pendingMemento.version, pendingMemento.mementoVersion, pendingMemento.memento);
                } else {
                    this.restoreRequiredRange(requiredRangeRatio, requiredRangeDirection, requiredRange);
                }

                // Maybe fire 'zoom:change-request' if the zoom-state has changed in this redraw:
                this.updateZoom({
                    source: 'chart-update', // FIXME(AG-16412): this is "probably" what caused, but we don't really know
                    sourceDetail: 'unspecified',
                });
            }),
            ctx.eventsHub.on('update:complete', ({ wasShortcut }) => {
                if (wasShortcut) return;
                if (this.pendingZoomEventSource) {
                    const source = this.pendingZoomEventSource;
                    this.ctx.fireEvent<AgZoomEvent>({ type: 'zoom', source, ...this.getMementoRanges() });
                    this.pendingZoomEventSource = undefined;
                }
            })
        );
    }

    // FIXME: should be private
    public toCoreZoomState(axisZoom: DeepReadonly<ZoomState>): CoreZoomState {
        const result: CoreZoomState = {};
        let ids: AxisID[];
        const { state } = this;

        if (this.independentAxes) {
            const xId = this.getPrimaryAxisId(ChartAxisDirection.X);
            const yId = this.getPrimaryAxisId(ChartAxisDirection.Y);
            ids = [];
            if (xId) ids.push(xId);
            if (yId) ids.push(yId);
        } else {
            ids = strictObjectKeys(state);
        }

        for (const id of ids) {
            const { direction } = state[id] ?? {};
            if (direction != undefined) {
                const zoom = axisZoom[direction];
                if (zoom) {
                    const { min, max } = zoom;
                    result[id] = { min, max, direction };
                }
            }
        }

        return result;
    }

    public createMemento(): ZoomMemento {
        return this.getMementoRanges();
    }

    public guardMemento(blob: unknown, messages: string[]): blob is ZoomMemento | undefined {
        if (blob == null) return true;
        if (!isObject(blob)) return false;

        const primaryX = this.getPrimaryAxis(ChartAxisDirection.X);
        const primaryY = this.getPrimaryAxis(ChartAxisDirection.Y);

        // Already validated by the optionsModule validator
        const zoomMementoDefs: OptionsDefs<ZoomMemento> = {
            rangeX: { start: rangeValidator(primaryX), end: defined },
            rangeY: { start: rangeValidator(primaryY), end: defined },
            ratioX: { start: defined, end: defined },
            ratioY: { start: defined, end: defined },
            autoScaledAxes: defined,
        };

        const { invalid } = validate(blob, zoomMementoDefs, '', { logger: this.ctx.logger });
        if (invalid.length > 0) {
            messages.push(...invalid.map(String));
            return false;
        }

        return true;
    }

    public restoreMemento(version: string, mementoVersion: string, memento: ZoomMemento | undefined) {
        // Do not reset the zoom if the memento is undefined. Only reset if an empty object is provided.
        if (memento == null) return;

        if (!this.axes || !this.didLayoutAxes) {
            this.pendingMemento = { version, mementoVersion, memento };
            return;
        }
        this.pendingMemento = undefined;

        // Migration from older versions can be implemented here.

        const zoom = definedZoomState(toZoomState(this.state));

        // `rangeY` memento restoration is handled in `ZoomAutoScale.onLoadMemento()`
        if (memento?.rangeX) {
            zoom.x = this.rangeToRatioDirection(ChartAxisDirection.X, memento.rangeX) ?? { min: 0, max: 1 };
        } else if (memento?.ratioX) {
            zoom.x = {
                min: memento.ratioX.start ?? 0,
                max: memento.ratioX.end ?? 1,
            };
        } else {
            zoom.x = { min: 0, max: 1 };
        }

        const { navigatorModule, zoomModule } = this;
        this.ctx.eventsHub.emit('zoom:load-memento', { zoom, memento, navigatorModule, zoomModule });

        const changes = this.toCoreZoomState(zoom);
        this.writeInitialZoom(toZoomState(changes));
        this.updateChanges({
            source: 'state-change',
            sourceDetail: 'internal-restoreMemento',
            changes,
            isReset: false,
        });
    }

    private writeInitialZoom(next: ZoomState | undefined) {
        const current = this.ctx.chartState.getValue('initialZoom');
        if (objectsEqual(current, next)) return;
        this.ctx.chartState.setValue('initialZoom', next);
    }

    private findAxis(axisId: AxisID): CartesianAxisLike | undefined {
        for (const a of this.axes) {
            if (a.id === axisId) return a;
        }
    }

    public getAxes() {
        return this.axes;
    }

    public setAxes(nextAxes: Parameters<typeof refreshCoreState>[0]) {
        // Snapshot outgoing per-axis zoom BEFORE replacing the axis arrays — otherwise the `state`
        // getter would read from freshly-constructed axis instances (defaults) whenever the chart
        // recreates axes with the same IDs.
        const oldState = this.state;

        // Saved {min,max} ratios don't translate across a scale-type change; preserving them lets
        // downstream `preserveDomain` collapse to a degenerate zoom.
        const previousTypeByAxisId = new Map<AxisID, string>();
        for (const axis of this.axes) {
            previousTypeByAxisId.set(axis.id, axis.type);
        }

        const { axes, allAxes } = this;
        axes.length = 0;
        allAxes.length = 0;
        for (const axis of nextAxes) {
            allAxes.push({ id: axis.id, direction: axis.direction });
            if ('range' in axis) {
                axes.push(axis);
            }
        }

        const adjustedOldState: Record<AxisID, CoreZoomEntry | undefined> = { ...oldState };
        for (const axis of axes) {
            const prevType = previousTypeByAxisId.get(axis.id);
            if (prevType !== undefined && prevType !== axis.type) {
                delete adjustedOldState[axis.id];
            }
        }

        const changes = refreshCoreState(nextAxes, adjustedOldState);
        this.state = changes;

        this.updateChanges({ source: 'chart-update', sourceDetail: 'internal-setAxes', changes, isReset: false });
    }

    public setIndependentAxes(independent = true) {
        this.independentAxes = independent;
    }

    public setNavigatorEnabled(enabled = true) {
        this.navigatorModule = enabled;
    }

    public setZoomModuleEnabled(enabled = true) {
        this.zoomModule = enabled;
    }

    public isNavigatorEnabled() {
        return this.navigatorModule;
    }

    public isZoomEnabled() {
        return this.zoomModule;
    }

    public updateZoom({ source, sourceDetail }: UpdateZoomSourcing, newZoom?: ZoomState): boolean {
        const changes = this.toCoreZoomState(newZoom ?? {});
        return this.updateChanges({ source, sourceDetail, changes, isReset: false });
    }

    private computeChangedAxesIds(newState: UpdateZoomChanges): readonly AxisID[] {
        const result: AxisID[] = [];
        const oldState = this.state;
        for (const id of strictObjectKeys(newState)) {
            const newAxisState = newState[id] ?? { min: 0, max: 1 };
            const oldAxisState = oldState[id];
            if (oldAxisState?.min !== newAxisState.min || oldAxisState.max !== newAxisState.max) {
                result.push(id);
            }
        }
        return result;
    }

    public updateChanges(params: UpdateZoomParams): boolean {
        const { source, sourceDetail, isReset, changes } = params;
        validateChanges(changes, this.ctx.logger);

        const changedAxes = this.computeChangedAxesIds(changes);
        const oldState: CoreZoomStateSafeRetrieval = deepClone(this.state);
        const newState: CoreZoomStateSafeRetrieval = deepClone(this.state);

        for (const id of changedAxes) {
            const axis = newState[id];
            if (axis != undefined) {
                axis.min = changes[id]?.min ?? 0;
                axis.max = changes[id]?.max ?? 1;
            }
        }
        this.state = newState;

        return this.dispatch(source, sourceDetail, changedAxes, isReset, oldState);
    }

    public resetZoom({ source, sourceDetail }: UpdateZoomSourcing) {
        const initial = this.ctx.chartState.getValue('initialZoom');
        const changes: UpdateZoomChanges = {};
        for (const axis of this.allAxes) {
            const directionZoom = pickDirectionZoom(initial, axis.direction);
            changes[axis.id] = directionZoom ? { min: directionZoom.min, max: directionZoom.max } : { min: 0, max: 1 };
        }
        this.updateChanges({ source, sourceDetail, changes, isReset: true });
    }

    public resetAxisZoom({ source, sourceDetail }: UpdateZoomSourcing, axisId: AxisID) {
        const axis = this.allAxes.find((a) => a.id === axisId);
        if (!axis) return;
        const initial = this.ctx.chartState.getValue('initialZoom');
        const directionZoom = pickDirectionZoom(initial, axis.direction);
        const entry = directionZoom ? { min: directionZoom.min, max: directionZoom.max } : { min: 0, max: 1 };
        this.updateChanges({
            source,
            sourceDetail,
            changes: { [axisId]: entry },
            isReset: true,
        });
    }

    public panToBBox(seriesRect: BBox, target: BoxBounds): boolean {
        if (!this.isZoomEnabled() && !this.isNavigatorEnabled()) return false;

        const zoom = toZoomState(this.state);
        if (zoom === undefined || (!zoom.x && !zoom.y)) return false;

        const { panToBBoxScalingMode } = this;
        const panIsPossible =
            seriesRect.width > 0 &&
            seriesRect.height > 0 &&
            (panToBBoxScalingMode !== PanToBBoxScalingModeEnum.None ||
                (Math.abs(target.width) <= Math.abs(seriesRect.width) &&
                    Math.abs(target.height) <= Math.abs(seriesRect.height)));
        if (!panIsPossible) {
            this.ctx.logger.warnOnce(`cannot pan to target BBox - chart too small?`);
            return false;
        }

        const newZoom: ZoomState = calcPanToBBoxRatios(panToBBoxScalingMode, seriesRect, zoom, target);
        const changes = this.toCoreZoomState(newZoom);
        return this.updateChanges({
            source: 'user-interaction',
            sourceDetail: 'internal-panToBBox',
            changes,
            isReset: false,
        });
    }

    // Fire this event to signal to listeners that the view is changing through a zoom and/or pan change.
    public fireZoomPanStartEvent(callerId: 'navigator' | 'zoom') {
        this.ctx.eventsHub.emit('zoom:pan-start', { callerId });
    }

    public updateWith(
        { source, sourceDetail }: UpdateZoomSourcing,
        direction: CartesianAxisDirection,
        fn: UpdateZoomWithFunction
    ) {
        const axis = this.getPrimaryAxis(direction);
        if (!axis) return;

        const extents = axis.scale.getDomainMinMax();
        if (!extents) return;

        const [min, max] = axis.visibleRange;
        const range = this.getRange(axis.id, { min, max });
        if (!range) return;

        const [domainStart, domainEnd] = extents;
        const { start: windowStart, end: windowEnd } = range;

        const result = fn({
            start: domainStart,
            end: domainEnd,
            windowStart: windowStart as Date | number,
            windowEnd: windowEnd as Date | number,
            source,
        });
        if (!this.isValidUpdateWithResult(result)) {
            this.resetZoom({ source, sourceDetail });
            return;
        }
        const [start, end] = result;

        const ratio = this.rangeToRatioAxis(axis, { start, end });
        if (!ratio) return;

        this.updateChanges({ source, sourceDetail, changes: { [direction]: ratio }, isReset: false });
    }

    public isValidUpdateWith(direction: CartesianAxisDirection, fn: UpdateZoomWithFunction, source: AgZoomEventSource) {
        const axis = this.getPrimaryAxis(direction);
        if (!axis) return true;

        const extents = axis.scale.getDomainMinMax();
        if (!extents) return true;

        const [min, max] = axis.visibleRange;
        const range = this.getRange(axis.id, { min, max });
        if (!range) return true;

        const [domainStart, domainEnd] = extents;
        const { start: windowStart, end: windowEnd } = range;

        const result = fn({
            start: domainStart,
            end: domainEnd,
            windowStart: windowStart as Date | number,
            windowEnd: windowEnd as Date | number,
            source,
        });
        if (!this.isValidUpdateWithResult(result)) {
            return true; // This will be treated as [undefined, undefined]
        }

        const [start, end] = result;

        let valid = true;
        if (start != null) {
            valid &&= start >= domainStart;
        }
        if (end != null) {
            valid &&= end <= domainEnd;
        }
        if (start != null && end != null) {
            valid &&= start <= end;
        }

        return valid;
    }

    /**
     * Snapshot of the per-axis zoom map built from axis-local state. Used by per-axis iteration
     * consumers (panner, scroller, toolbar independent-axes branch). New code should prefer reading
     * `chartState.zoom` (per-direction) or `axis.getZoom()` (per-axis).
     */
    public getAxisZooms(): CoreZoomStateSafeRetrieval {
        return this.state;
    }

    public getAxisZoom(axisId: AxisID): ZoomMinMax {
        return this.state[axisId] ?? { min: 0, max: 1 };
    }

    public getPrimaryAxisId(direction: CartesianAxisDirection) {
        return this.getPrimaryAxis(direction)?.id;
    }

    private getBoundSeries() {
        const xAxis = this.getPrimaryAxis(ChartAxisDirection.X);
        const yAxis = this.getPrimaryAxis(ChartAxisDirection.Y);

        let boundSeries: Set<ISeries<any, any, any>>;

        if (this.independentAxes) {
            const xBoundSeries = new Set(xAxis?.boundSeries ?? []);
            const yBoundSeries = new Set(yAxis?.boundSeries ?? []);

            boundSeries = new Set();
            for (const series of xBoundSeries) {
                if (yBoundSeries.has(series)) {
                    boundSeries.add(series);
                }
            }
        } else {
            boundSeries = new Set([...(xAxis?.boundSeries ?? []), ...(yAxis?.boundSeries ?? [])]);
        }

        return boundSeries;
    }

    public constrainZoomToItemCount(
        zoom: DefinedZoomState,
        minVisibleItems: number,
        shouldAutoscale: boolean
    ): DefinedZoomState {
        let xVisibleRange: [number, number] = [zoom.x.min, zoom.x.max];
        let yVisibleRange: [number, number] | undefined = shouldAutoscale ? undefined : [zoom.y.min, zoom.y.max];
        for (const series of this.getBoundSeries()) {
            const nextZoom = series.getZoomRangeFittingItems(xVisibleRange, yVisibleRange, minVisibleItems);
            if (nextZoom == null) continue;

            xVisibleRange = nextZoom.x;
            yVisibleRange = nextZoom.y;
        }

        const x = { min: clamp(0, xVisibleRange[0], 1), max: clamp(0, xVisibleRange[1], 1) };
        const y = yVisibleRange
            ? { min: clamp(0, yVisibleRange[0], 1), max: clamp(0, yVisibleRange[1], 1) }
            : undefined;
        return definedZoomState({ x, y });
    }

    public isVisibleItemsCountAtLeast(
        zoom: DefinedZoomState,
        minVisibleItems: number,
        opts: { autoScaleYAxis: boolean; includeYVisibleRange: boolean }
    ): boolean {
        const boundSeries = this.getBoundSeries();

        // Note: `series.getVisibleItems` has much better performance when `autoScaling.enabled: true`, because it only
        // needs to consider the X axis to count the number of visible items.
        const xVisibleRange: [number, number] = [zoom.x.min, zoom.x.max];
        const yVisibleRange: [number, number] | undefined =
            !opts.includeYVisibleRange && opts.autoScaleYAxis ? undefined : [zoom.y.min, zoom.y.max];

        let visibleItemsCount = 0;

        for (const series of boundSeries) {
            const remainingItems = minVisibleItems - (visibleItemsCount ?? 0);
            const seriesVisibleItems = series.getVisibleItems(xVisibleRange, yVisibleRange, remainingItems);
            visibleItemsCount += seriesVisibleItems;
            if (visibleItemsCount >= minVisibleItems) return true;
        }

        return boundSeries.size === 0;
    }

    private getMementoRanges() {
        const zoom = definedZoomState(toZoomState(this.state));
        const memento = {
            rangeX: this.getRangeDirection(ChartAxisDirection.X, zoom.x),
            rangeY: this.getRangeDirection(ChartAxisDirection.Y, zoom.y),
            ratioX: { start: zoom.x.min, end: zoom.x.max },
            ratioY: { start: zoom.y.min, end: zoom.y.max },
            autoScaledAxes: undefined,
        };

        this.ctx.eventsHub.emit('zoom:save-memento', { memento });
        return memento;
    }

    private restoreRequiredRange(
        requiredRangeRatio: number,
        requiredRangeDirection: ChartAxisDirection,
        requiredRange: number
    ) {
        const { lastRestoredRequiredRange, lastRestoredRequiredRangeDirection } = this;

        // When the underlying required range changes (e.g. user changed barWidth), reset the iteration counter
        // so the new range is applied. Layout-triggered re-layouts only change the ratio (via chart dimension
        // changes) without changing the required range itself, so the counter still prevents those oscillations.
        // @see AG-17008
        if (this.lastRequiredRange !== requiredRange) {
            this.lastRequiredRange = requiredRange;
            this.restoreRequiredRangeIterations = 0;
        }

        const requiredZoom = Math.min(1, 1 / requiredRangeRatio);

        // Prevent infinite loops where an x-axis label with a different height becomes visible, causing a change in
        // the chart height. This triggers the nice algorithm to change the y-axis label widths which changes the
        // width of the chart. This width change then triggers this function again with a different zoom ratio
        // as a proportion of that new chart width. This changes the chart's zoom, making the taller x-axis
        // label hidden again, creating an infinite loop.
        // A full-range application (the content fits) changes nothing visible and cannot start that loop, so it must
        // not consume the one-shot budget — otherwise a genuine overflow zoom after an external resize is suppressed.
        // @see AG-16803
        if (requiredZoom < 1) {
            this.restoreRequiredRangeIterations += 1;
        }

        const directionInvalid =
            requiredRangeDirection !== ChartAxisDirection.X && requiredRangeDirection !== ChartAxisDirection.Y;
        const requiredRangeUnchanged =
            lastRestoredRequiredRangeDirection === requiredRangeDirection &&
            lastRestoredRequiredRange === requiredRangeRatio;
        const requiredRangeUnset =
            requiredRangeRatio === 0 && (lastRestoredRequiredRange == null || lastRestoredRequiredRange === 0);

        if (
            directionInvalid ||
            requiredRangeUnchanged ||
            requiredRangeUnset ||
            this.restoreRequiredRangeIterations > 1
        ) {
            this.restoreRequiredRangeIterations = 0;
            return;
        }

        const crossAxisId = this.getPrimaryAxisId(requiredRangeDirection);
        if (!crossAxisId) return;

        const crossAxisZoom = this.state[crossAxisId] ?? { min: 0, max: 1 };

        let min = 0;
        let max = 1;

        // For vertical bars, pin to the left and extend right until the right reaches max, then extend left.
        // For horizontal bars, pin to the top and extend down until the bottom reaches max, then extend up.
        if (requiredRangeDirection === ChartAxisDirection.X) {
            min = clamp(0, 1 - requiredZoom, crossAxisZoom.min);
            max = clamp(0, min + requiredZoom, 1);
        } else {
            max = Math.min(1, crossAxisZoom.max);
            min = max - requiredZoom;
            if (min < 0) {
                max -= min;
                min = 0;
            }
            min = clamp(0, min, 1);
            max = clamp(0, max, 1);
        }

        this.lastRestoredRequiredRange = requiredRangeRatio;
        this.lastRestoredRequiredRangeDirection = requiredRangeDirection;

        const zoom = { [requiredRangeDirection]: { min, max } };
        const changes = this.toCoreZoomState(zoom);
        this.writeInitialZoom(toZoomState(changes));

        this.updateChanges({
            source: 'state-change',
            sourceDetail: 'internal-requiredWidth',
            changes,
            isReset: false,
        });
    }

    private constrainZoomToRequiredWidth(event: ZoomChangeRequestEvent) {
        if (this.lastRestoredRequiredRange == null || this.lastRestoredRequiredRangeDirection == null) return;

        const crossAxisId = this.getPrimaryAxisId(this.lastRestoredRequiredRangeDirection);
        if (!crossAxisId) return;

        const zoom = event.state[crossAxisId];
        const oldState = event.oldState[crossAxisId];
        if (!zoom || !oldState) return;

        const delta = zoom.max - zoom.min;
        const minDelta = 1 / this.lastRestoredRequiredRange;
        if (Math.abs(delta - minDelta) < 1e-12 || delta <= minDelta) return;

        event.constrainChanges({
            [crossAxisId]: {
                direction: this.lastRestoredRequiredRangeDirection,
                min: oldState.min,
                max: oldState.min + minDelta,
            },
        });
    }

    private dispatch(
        source: AgZoomEventSource,
        sourceDetail: ZoomEventSourceDetail,
        changedAxes: readonly AxisID[],
        isReset: boolean,
        oldState: CoreZoomStateSafeRetrieval
    ): boolean {
        const { x, y } = toZoomState(this.state) ?? {};
        const state = this.state;
        let constrainedState: typeof state | undefined;

        const debug = this.debug;
        const zoomManager = this;
        const event = {
            source,
            sourceDetail,
            isReset,
            changedAxes,
            state,
            oldState,
            x,
            y,
            stateAsDefinedZoom(): DefinedZoomState {
                return definedZoomState(toZoomState(event.state));
            },
            constrainZoom(restrictions: ZoomState): void {
                this.constrainChanges(zoomManager.toCoreZoomState(restrictions));
            },
            constrainChanges(restrictions: ZoomChangeState): void {
                if (debug.check()) {
                    debug('ZoomManager.constrainChanges()', state, '->', restrictions, new Error().stack);
                }
                constrainedState ??= deepClone(state);
                for (const id of strictObjectKeys(restrictions)) {
                    const src = restrictions[id];
                    const dst = constrainedState[id];
                    if (src && dst) {
                        dst.min = src.min;
                        dst.max = src.max;
                    }
                }
                event.state = constrainedState;
            },
        } satisfies ZoomChangeRequestEvent;

        this.ctx.eventsHub.emit('zoom:change-request', event);

        if (constrainedState && !areEqualCoreZooms(state, constrainedState)) {
            this.state = constrainedState;
        }

        // Write to chartState only when the stored value actually differs — `toZoomState` allocates
        // a fresh object each call, and ReactiveState notifies on reference inequality, so an
        // unconditional write would wake every `zoom`-subscribed observer on no-op reconciliations.
        const acceptedZoom = toZoomState(this.state);
        if (!objectsEqual(this.ctx.chartState.getValue('zoom'), acceptedZoom)) {
            this.ctx.chartState.setValue('zoom', acceptedZoom);
        }

        const changeAccepted: boolean = !areEqualCoreZooms(oldState, this.state);
        if (changeAccepted) {
            this.ctx.eventsHub.emit('zoom:change-complete', { source, sourceDetail, x: acceptedZoom?.x });
            this.pendingZoomEventSource = source; // emit API AgZoomEvent when the redraw completes
        }
        return changeAccepted;
    }

    private getRange(axisId: AxisID, ratio: ZoomMinMax) {
        return this.getRangeAxis(this.findAxis(axisId), ratio);
    }

    private getRangeDirection(direction: CartesianAxisDirection, ratio: ZoomMinMax) {
        return this.getRangeAxis(this.getPrimaryAxis(direction), ratio);
    }

    private getRangeAxis(axis: CartesianAxisLike | undefined, ratio: ZoomMinMax): ZoomMementoRange | undefined {
        if (!axis) return;

        const extents = this.getDomainPixelExtents(axis);
        if (!extents) return;

        const [d0, d1] = extents;

        let start;
        let end;

        if (d0 <= d1) {
            start = axis.scale.invertWithPercentage(0); // 0 is the start of the visible axis
            end = axis.scale.invertWithPercentage(d0 + (d1 - d0) * ratio.max);
        } else {
            start = axis.scale.invertWithPercentage(d0 - (d0 - d1) * ratio.min);
            end = axis.scale.invertWithPercentage(0);
        }

        return { start, end };
    }

    public rangeToRatioDirection(direction: CartesianAxisDirection, range: ZoomMementoRange): ZoomMinMax | undefined {
        return this.rangeToRatioAxis(this.getPrimaryAxis(direction), range);
    }

    private rangeToRatioAxis(axis: CartesianAxisLike | undefined, range: ZoomMementoRange): ZoomMinMax | undefined {
        if (!axis) return;

        const extents = this.getDomainPixelExtents(axis);
        if (!extents) return;

        const [d0, d1] = extents;
        const { scale } = axis;
        const { start, end } = range;

        const startGrouping = getGrouping(start);
        const endGrouping = getGrouping(end);
        const scaleWidth = scale.bandwidth === 0 ? (scale.step ?? 0) : (scale.bandwidth ?? 0);

        const [startAlignment = ScaleAlignment.Leading, endAlignment = ScaleAlignment.Trailing] = rangeAlignment(
            startGrouping.value,
            endGrouping.value
        );

        let r0 = d0;
        let r1 = d1;

        if (startGrouping.value != null) {
            if (isGrouping(start)) {
                r0 = scale.convert(startGrouping.value) + scaleWidth * startGrouping.groupPercentage;
            } else {
                r0 = scale.convert(start, { alignment: startAlignment, alignmentExclusive: true });
            }
        }

        if (endGrouping.value != null) {
            if (isGrouping(end)) {
                r1 = scale.convert(endGrouping.value) + scaleWidth * endGrouping.groupPercentage;
            } else {
                // Note we do not use `scaleWidth` here as adding the step is not valid in this case.
                r1 = scale.convert(end, { alignment: endAlignment, alignmentExclusive: true }) + (scale.bandwidth ?? 0);
            }
        }

        if (!isFiniteNumber(r0) || !isFiniteNumber(r1)) return;

        const [dMin, dMax] = [Math.min(d0, d1), Math.max(d0, d1)];

        r0 = clamp(dMin, r0, dMax);
        r1 = clamp(dMin, r1, dMax);

        const diff = d1 - d0;
        if (diff === 0) return;

        const min = Math.abs((r0 - d0) / diff);
        const max = Math.abs((r1 - d0) / diff);
        if (min >= max) return;

        return { min, max };
    }

    public getPrimaryAxis(direction: CartesianAxisDirection) {
        return this.axes?.find((a) => a.direction === direction);
    }

    private getDomainPixelExtents(axis: CartesianAxisLike) {
        const [d0, d1] = axis.scale.range;

        if (!isFiniteNumber(d0) || !isFiniteNumber(d1)) return;

        return [d0, d1];
    }

    private isValidUpdateWithResult(result: unknown): result is [Date | number | undefined, Date | number | undefined] {
        if (result == null) return false;
        if (!Array.isArray(result)) return false;
        if (result.length < 1) return false;
        return result.every((r) => r == null || typeof r === 'number' || isValidDate(r));
    }
}
